import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { dayIn, formatWeek, shiftWeek, weekOf } from "../../lib/missions";
import { ACTIVITIES, ACTIVITY_LABELS, attendanceErrorMessage, formatMinutes } from "../../lib/attendance";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Clock, Download, Loader2, RefreshCw } from "lucide-react";

// « Temps » tab of Missions: the times of one week, per person and per mission. The server returns one's own times, or
// everyone's with missions:view_all, who may also download them as CSV (journalled).
export default function AttendanceSummary() {
  const { user } = useAuth();
  const today = dayIn(new Date().toISOString());
  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get("week");
  const days = useMemo(() => weekOf(/^\d{4}-\d{2}-\d{2}$/.test(asked || "") ? asked : today), [asked, today]);
  const setDays = (next) => setSearchParams((p) => { const out = new URLSearchParams(p); out.set("week", next[0]); return out; }, { replace: true });
  const [state, setState] = useState({ phase: "loading", data: null, error: "" });
  const [downloading, setDownloading] = useState(false);
  const seq = useRef(0);
  const canExport = hasPermission(user, "missions:view_all");

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const { data } = await api.get("/attendance", { params: { from: days[0], to: days[6] } });
      if (mine === seq.current) setState({ phase: "ready", data, error: "" });
    } catch (e) {
      if (mine === seq.current) setState({ phase: "error", data: null, error: attendanceErrorMessage(e, "Impossible de charger les temps.") });
    }
  }, [days]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/attendance/export.csv", { params: { from: days[0], to: days[6] }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `temps-${days[0]}-${days[6]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Impossible de télécharger l'export.");
    } finally {
      setDownloading(false);
    }
  };

  const data = state.data;
  return (
    <div className="space-y-3" data-testid="attendance-summary">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
          <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Semaine précédente" onClick={() => setDays(shiftWeek(days, -1))} data-testid="attendance-prev"><ChevronLeft size={16} /></Button>
          <span className="px-2 text-sm font-medium text-slate-900 whitespace-nowrap" data-testid="attendance-week">{formatWeek(days)}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Semaine suivante" onClick={() => setDays(shiftWeek(days, 1))} data-testid="attendance-next"><ChevronRight size={16} /></Button>
        </div>
        {!days.includes(today) && <Button variant="outline" size="sm" className="h-9" onClick={() => setDays(weekOf(today))}>Cette semaine</Button>}
        {canExport && (
          <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={download} disabled={downloading} data-testid="attendance-export">
            {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}Exporter (CSV)
          </Button>
        )}
      </div>
      {data && <p className="text-xs text-slate-500">{data.all ? "Les temps de toutes les personnes." : "Vos temps."} Pointages commencés dans la semaine.</p>}

      {state.phase === "loading" && !data && <div className="space-y-2"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>}
      {state.phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {data && data.people.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="attendance-empty">
          <Clock className="mx-auto text-slate-300" size={24} />
          <p className="text-sm text-slate-600">Aucun temps pointé cette semaine.</p>
        </Card>
      )}
      {data && data.people.length > 0 && (
        <>
          <Card className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="attendance-totals">
            {[...ACTIVITIES, "total"].map((a) => (
              <div key={a}>
                <div className="text-xs text-slate-500">{a === "total" ? "Total" : ACTIVITY_LABELS[a]}</div>
                <div className="text-base font-semibold text-slate-900">{formatMinutes(data.totals[a])}</div>
              </div>
            ))}
          </Card>
          <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="attendance-people">
            {data.people.map((p) => (
              <div key={`${p.person.kind}:${p.person.id}`} className="p-4 space-y-2" data-testid={`attendance-person-${p.person.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <span className="font-medium text-slate-900">{p.person.name}{p.person.kind === "collaborator" && <span className="text-xs font-normal text-slate-500"> · sans compte CRM</span>}</span>
                  <span className="text-sm font-semibold text-slate-900">{formatMinutes(p.totals.total)}</span>
                </div>
                <ul className="space-y-1">
                  {p.missions.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-3 text-sm">
                      <Link to={`/missions/${m.id}`} className="min-w-0 truncate text-slate-700 hover:underline">{m.reference} · {m.title}</Link>
                      <span className="shrink-0 text-xs text-slate-500 flex items-center gap-1">
                        {ACTIVITIES.filter((a) => m.totals[a]).map((a) => `${ACTIVITY_LABELS[a]} ${formatMinutes(m.totals[a])}`).join(" · ")}
                        {m.validated && <CheckCircle2 size={13} className="text-emerald-600" aria-label="Temps validés" />}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
